import { useCallback, useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { QUERY_KEYS } from '~/constants/react-query'
import { useRequiredActiveOrg } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useServerRequests } from '~/hooks/useRequests'
import { downloadFileFromUrl } from '~/utils/files'
import { sendTrackingData } from '~/utils/tracking'

export type ReportFormat = 'csv' | 'pdf'

type ReportResponse = { userFriendlyFileName: string } & (
  | {
      isLongRunningOperation: true
      csvDownloadUrl: null
      jobId: string
    }
  | {
      isLongRunningOperation: false
      csvDownloadUrl: string
      jobId: null
    }
)

type ReportJobStatusResponse = {
  csvDownloadUrl: string | null
  failed: boolean
}

const JOB_POLLING_INTERVAL_MS = 3000 // 3 seconds

export const ACCOUNTING_REPORT_PATHS = {
  statement: '/api/org/accounting/statement',
  transactions: '/api/org/accounting/csv-report',
  tokens: '/api/org/accounting/tokens/csv-report',
  lots: '/api/org/accounting/lots/csv-report',
  lotTimeline: '/api/org/accounting/lot-timeline/csv-report',
  realizedGains: '/api/org/accounting/gl-report',
} as const

export type AccountingReportKind = keyof typeof ACCOUNTING_REPORT_PATHS

/**
 * Requests one accounting report and delivers the file.
 *
 * Reports that outrun the request are queued: the server emails the file when
 * it lands, and polling here downloads it in-tab too. `autoDownload: false`
 * skips that — the Reports tab lists the finished file instead.
 */
export const useGenerateReport = ({
  kind,
  filterParams,
  autoDownload = true,
}: {
  kind: AccountingReportKind
  filterParams: Record<string, string>
  autoDownload?: boolean
}) => {
  const [isGenerating, setIsGenerating] = useState(false)

  const org = useRequiredActiveOrg()
  const queryClient = useQueryClient()

  const authUser = useAuthUser()
  const userEmail = authUser.user!.email

  const { serverGetRequest } = useServerRequests()

  // One entry per queued job, so overlapping exports poll independently.
  const pollingTimeoutsRef = useRef(new Set<ReturnType<typeof setTimeout>>())
  const isUnmountedRef = useRef(false)

  // A queued job outlives the component that asked for it, so unmount has to
  // drop its scheduled polls and flag any in-flight one. Unmount is only
  // observable from an effect.
  // eslint-disable-next-line local/noUseEffect -- teardown of timers this hook owns
  useEffect(() => {
    const timeouts = pollingTimeoutsRef.current
    return () => {
      isUnmountedRef.current = true
      timeouts.forEach(clearTimeout)
      timeouts.clear()
    }
  }, [])

  // Every report writes a row the Reports tab lists, so the list is stale the
  // moment one is requested.
  const invalidateReports = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_ACCOUNTING_REPORTS, org.id],
      }),
    [queryClient, org.id],
  )

  const pollJobStatus = useCallback(
    async (jobId: string, userFriendlyFileName: string) => {
      const response = await serverGetRequest<ReportJobStatusResponse>({
        path: `/api/org/accounting/csv/jobs/${jobId}`,
        errorMessage: 'Failed to check job status',
        params: {
          orgId: org.id,
        },
      })

      // Checked after the request, not before: a poll already in flight at
      // unmount would otherwise still toast and download into a page the user
      // has left.
      if (isUnmountedRef.current) return

      if (!response) {
        toast.error('Failed to check job status')
        return
      }

      const { csvDownloadUrl, failed } = response.data

      if (failed) {
        // Jobs don't retry, so a failure is final
        toast.error('Failed to generate report', {
          description: 'Please try again or contact support@splits.org',
        })
        await invalidateReports()
      } else if (csvDownloadUrl) {
        if (autoDownload) {
          downloadFileFromUrl(csvDownloadUrl, userFriendlyFileName)
        }
        await invalidateReports()
      } else {
        const timeout = setTimeout(() => {
          pollingTimeoutsRef.current.delete(timeout)
          pollJobStatus(jobId, userFriendlyFileName)
        }, JOB_POLLING_INTERVAL_MS)
        pollingTimeoutsRef.current.add(timeout)
      }
    },
    [org.id, serverGetRequest, autoDownload, invalidateReports],
  )

  const generate = async (format: ReportFormat) => {
    setIsGenerating(true)

    const response = await serverGetRequest<ReportResponse>({
      path: ACCOUNTING_REPORT_PATHS[kind],
      params: {
        orgId: org.id,
        format,
        ...filterParams,
      },
      errorMessage: 'Failed to generate report',
    })

    await invalidateReports()

    if (response) {
      sendTrackingData('accountingReportRequested', {
        orgId: org.id,
        kind,
        format,
        isLongRunningOperation: response.data.isLongRunningOperation,
      })

      if (response.data.isLongRunningOperation) {
        toast.success(`Your report is being generated`, {
          description: autoDownload
            ? `We'll email you at ${userEmail} when it's ready`
            : "It'll appear here, and we'll email you when it's ready",
        })
        // The job is queued and emails on completion, so free the button
        // immediately; polling continues in the background so the file still
        // downloads in-tab when it's ready.
        setIsGenerating(false)
        pollJobStatus(response.data.jobId, response.data.userFriendlyFileName)
      } else {
        if (autoDownload) {
          downloadFileFromUrl(
            response.data.csvDownloadUrl,
            response.data.userFriendlyFileName,
          )
        }
        setIsGenerating(false)
      }
    } else {
      setIsGenerating(false)
    }
  }

  return {
    isGenerating,
    generateCSV: async () => generate('csv'),
    generatePDF: async () => generate('pdf'),
    generate,
  }
}
